import { useCallback, useEffect, useState } from 'react';
import { listAllNotesAdmin, deleteNote, type AdminNote, NotesApiError } from '../api/notes';
import { formatRelativeTime } from '../lib/relative-time';

interface AdminNotesPageProps {
  token: string;
}

export function AdminNotesPage({ token }: AdminNotesPageProps) {
  const [notes, setNotes] = useState<AdminNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAllNotesAdmin(token);
      setNotes(rows);
    } catch (err) {
      const message =
        err instanceof NotesApiError && err.status === 401
          ? 'Invalid admin token. Check the URL ?token=… parameter.'
          : err instanceof Error
          ? err.message
          : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingIds((prev) => new Set(prev).add(id));
      try {
        await deleteNote(id, token);
        setNotes((prev) =>
          prev.map((n) => (n.id === id ? { ...n, deletedAt: Math.floor(Date.now() / 1000) } : n)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        alert(`Delete failed: ${message}`);
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [token],
  );

  return (
    <div
      data-testid="admin-notes-page"
      style={{
        maxWidth: 920,
        margin: '0 auto',
        padding: 'var(--space-8)',
        background: 'var(--background)',
        color: 'var(--text)',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <h1 style={{ marginBottom: 'var(--space-2)' }}>Notes — moderation</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-6)' }}>
        Listing the most recent {notes.length} notes (active + soft-deleted).
      </p>

      {loading ? (
        <p data-testid="admin-loading">Loading…</p>
      ) : error !== null ? (
        <p data-testid="admin-error" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      ) : notes.length === 0 ? (
        <p>No notes yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {notes.map((note) => {
            const isDeleted = note.deletedAt !== null;
            const isDeleting = deletingIds.has(note.id);
            return (
              <li
                key={note.id}
                data-testid={`admin-note-${note.id}`}
                style={{
                  background: isDeleted ? 'var(--surface-hover)' : 'var(--surface-raised)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  opacity: isDeleted ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  <strong style={{ fontFamily: 'var(--font-serif)' }}>{note.nickname}</strong>
                  <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {note.landmarkId} · {formatRelativeTime(note.createdAt)}
                  </code>
                </div>
                <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: 'var(--space-3)' }}>
                  {note.body}
                </p>
                {isDeleted ? (
                  <span
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: '0.8rem',
                      fontStyle: 'italic',
                    }}
                  >
                    deleted {formatRelativeTime(note.deletedAt!)}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleDelete(note.id)}
                    disabled={isDeleting}
                    data-testid={`admin-delete-${note.id}`}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--error)',
                      color: '#fffdf2',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    }}
                  >
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
